import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, NO_ZOOM_FONT, TOUCH_MIN, touchField} from "../../../../../theme/modern/touchTargets"

/**
 * Ported from `record.css` `#sectDispo` / `.interp` / `.interp-text`, plus the
 * `.dock` block used by the sidebar layout.
 *
 * The chips carry the prototype's short labels ("Normal", "Non-ref", …) rather
 * than production's full interpretation sentences, which are far too long for a
 * pill. The sentence a chip writes is still shown in full — in its `title` and,
 * once picked, in the text field beneath the row.
 */
export default makeStyles((theme: ModernTheme) => ({
    sect: {
        background: theme.modern.panel,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 12,
        padding: "9px 12px",
        boxShadow: theme.modern.shadow,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        overflow: "auto",
    },
    sect_head: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: 6,
    },
    /** Keeps the Statements button and the save chip on one baseline. */
    sect_head_actions: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minWidth: 0,
    },
    microlabel: {
        font: `600 10px/1 ${theme.modern.sans}`,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        color: theme.modern.ink2,
    },
    savestate: {
        font: `500 11px ${theme.modern.sans}`,
        color: theme.modern.ok,
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        whiteSpace: "nowrap",
    },
    savestate__dirty: {
        color: theme.modern.warn,
    },

    /**
     * Positioning context for <StatementSuggestions/>, which docks to the
     * bottom of the field. `flex: 1 1 auto` moves here from the textarea so the
     * wrapper takes the growth the textarea used to, and the textarea fills it.
     */
    field: {
        position: "relative",
        flex: "1 1 auto",
        display: "flex",
        minHeight: 0,
    },
    /** Opens the full statement list, for anyone who would rather browse. */
    statements_button: {
        flex: "none",
        cursor: "pointer",
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 5,
        background: theme.modern.panel2,
        color: theme.modern.ink2,
        font: `600 10px/1 ${theme.modern.sans}`,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        padding: "5px 8px",

        "&:hover": {borderColor: theme.modern.accent, color: theme.modern.accent},
        "&:disabled": {opacity: .5, cursor: "default"},
    },
    /**
     * Undo as an icon only — Word's own undo arrow (Fluent "arrow undo"), the
     * undo reviewers already know. Its name is in the tooltip and aria-label.
     */
    undo_button: {
        flex: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // no taller than Statements, so the heading row keeps its height
        width: 22,
        height: 20,
        padding: 0,
        cursor: "pointer",
        border: "1px solid transparent",
        borderRadius: 5,
        background: "transparent",
        color: theme.modern.ink,

        // the icon is drawn as a filled outline, so it takes colour as fill
        "& svg": {width: 17, height: 17, fill: "currentColor"},

        "&:hover:not(:disabled)": {background: theme.modern.panel2, borderColor: theme.modern.line},
        "&:disabled": {color: theme.modern.ink3, opacity: .45, cursor: "default"},

        // On a tablet it is the only way back from a removal (no Ctrl+Z), so it
        // is finger-sized there — with negative margins, so the bigger target
        // takes no more room in the heading row than it does on a desktop.
        [COARSE]: {width: TOUCH_MIN, height: TOUCH_MIN, margin: "-12px -8px", "& svg": {width: 20, height: 20}},
    },
    /**
     * The same list, offered from the corner of the field being written in.
     *
     * The header button is easy to miss while looking at the text, which is
     * where the attention is. This sits where the writing ends, so the way out
     * of a half-remembered statement is in view at the moment it is needed.
     *
     * Opaque rather than transparent: it floats over the text, and the
     * findings are one statement per line, so the last line reaching this far
     * right is possible even if it is not common.
     */
    expand: {
        position: "absolute",
        right: 5,
        bottom: 5,
        zIndex: 2,
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        cursor: "pointer",
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 5,
        background: theme.modern.panel,
        color: theme.modern.ink3,
        font: `600 9px/1 ${theme.modern.sans}`,
        letterSpacing: ".07em",
        textTransform: "uppercase",
        padding: "4px 6px",
        boxShadow: theme.modern.shadow,

        "&:hover": {
            borderColor: theme.modern.accent,
            color: theme.modern.accent,
            background: theme.modern.accentSoft,
        },

        [COARSE]: {minHeight: TOUCH_MIN, padding: "0 10px"},
    },
    /** Expanded: the button is the way back, so it reads as pressed. */
    expand__on: {
        borderColor: theme.modern.accentLine,
        background: theme.modern.accentSoft,
        color: theme.modern.accent,
    },
    /**
     * The diagonal expand arrow, as a character rather than an icon asset.
     *
     * It says "make this bigger", which is exactly what the button does — a
     * grid glyph would suggest a catalogue, which is the header button's job,
     * not this one's.
     */
    expand_glyph: {
        display: "inline-block",
        flex: "none",
        font: `400 11px/1 ${theme.modern.sans}`,
        // the arrow sits high on its baseline next to uppercase text
        transform: "translateY(.5px)",
    },
    textarea: {
        width: "100%",
        resize: "none",
        flex: "1 1 auto",
        minHeight: 62,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: theme.modern.radius,
        background: theme.modern.panel2,
        color: theme.modern.ink,
        font: `400 12.5px/1.45 ${theme.modern.sans}`,
        padding: "7px 9px",

        "&:focus": {
            outline: `2px solid ${theme.modern.accent}`,
            outlineOffset: -1,
        },
        "&::placeholder": {color: theme.modern.ink3},
        "&:disabled": {color: theme.modern.ink2, cursor: "default"},

        // the conclusion field. At 12.5 px, tapping into it zoomed the whole
        // page on iOS and the reviewer had to pinch back out to see the trace
        // they were writing about.
        [COARSE]: {fontSize: NO_ZOOM_FONT, lineHeight: 1.45},
    },
    textarea__grow: {
        flex: "none",
        overflowY: "hidden",
    },
    /**
     * Grown to fit the whole report, but not without limit.
     *
     * `fitTextarea` sets an explicit pixel height from the content; this caps
     * it, so a long report stops growing and scrolls instead of pushing the
     * trace off the screen.
     *
     * Kept below the band's own 64vh ceiling by enough to clear this card's
     * heading, conclusion row and counter — roughly 150 px — so the two limits
     * never fight over the same pixels.
     *
     * `overflowY: auto` matters: the mobile grow variant hides overflow, which
     * is safe there because the page itself scrolls, and would silently clip
     * text here.
     */
    textarea__expanded: {
        flex: "none",
        maxHeight: "44vh",
        overflowY: "auto",
    },
    textarea__dock: {
        minHeight: 96,
        font: `400 13px/1.5 ${theme.modern.sans}`,
        padding: "8px 10px",

        [COARSE]: {fontSize: NO_ZOOM_FONT},
    },
    /**
     * Room after the last word of every line for its remove button, so the
     * button never sits on text. Declared after __dock so this right padding
     * wins over that one's.
     */
    textarea__marks: {
        paddingRight: 26,

        [COARSE]: {paddingRight: 32},
    },
    /**
     * The shading over the statement the pointer is on. Laid over the text,
     * see-through and never in the way of a click.
     */
    highlight: {
        position: "absolute",
        zIndex: 1,
        pointerEvents: "none",
        borderRadius: 3,
        background: `${theme.modern.crit}1F`,
    },
    /**
     * An invisible copy of the textarea, laid out the same way, measured to
     * find where each line starts. Never seen, never in the way.
     */
    mirror: {
        position: "absolute",
        top: 0,
        left: 0,
        visibility: "hidden",
        pointerEvents: "none",
        boxSizing: "border-box",
        border: 0,
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
        wordWrap: "break-word",
    },
    /**
     * A statement's remove button, just after the end of its text. Faint
     * until the pointer is on its statement; on a touch screen, where there is
     * no hover, always in view.
     */
    remove: {
        position: "absolute",
        zIndex: 3,
        width: 20,
        height: 18,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        cursor: "pointer",
        border: "1px solid transparent",
        borderRadius: 4,
        background: "transparent",
        color: theme.modern.ink3,
        opacity: .6,

        "& svg": {
            width: 10,
            height: 10,
            fill: "none",
            stroke: "currentColor",
            strokeWidth: 3,
            strokeLinecap: "round",
        },

        [COARSE]: {opacity: 1, color: theme.modern.crit, width: 26, height: 22},
    },
    remove__hover: {
        opacity: 1,
        color: theme.modern.crit,
        background: theme.modern.critSoft,
        borderColor: theme.modern.crit,
    },
    counter: {
        alignSelf: "flex-end",
        font: `400 10px ${theme.modern.mono}`,
        color: theme.modern.ink3,
        marginTop: 3,
    },

    // ---- conclusion picker ----
    // In the card it is one wrapping row (label, chips, free text); in the dock
    // it is a proper column, because the dock already gives it half the width.
    dispo_row: {
        display: "flex",
        alignItems: "center",
        gap: "10px 12px",
        flexWrap: "wrap",
        marginTop: 7,
        minWidth: 0,
    },
    dispo_column: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "8px",
        minWidth: 0,
    },
    dispo_title: {
        flex: "none",
        alignSelf: "center",
    },
    interp_grid: {
        display: "flex",
        gap: "5px",
        flexWrap: "wrap",
        minWidth: 0,
    },
    /**
     * Sized to keep all six chips on ONE line in the card layout, which is
     * worth more than the pixels it saves: the card is the tallest of the three
     * in the top band, so it sets the band's height, and the other two stretch
     * to match it and stand half empty. At 11.5 px with 9 px of padding the row
     * needed 614 px against 596 px available — eighteen short, and that near
     * miss cost a whole second row plus its gap, about 45 px, pushing the trace
     * down the page for nothing.
     *
     * Longer translations (`Normale età`, `Норма за віком`) may still wrap, so
     * this is sized with headroom rather than to the exact 18 px, and a wrapped
     * row remains correct — only less tight.
     */
    interp: {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        border: `1px solid ${theme.modern.line}`,
        background: theme.modern.panel2,
        borderRadius: 7,
        padding: "4px 8px",
        textAlign: "left",
        font: `600 11px ${theme.modern.sans}`,
        color: theme.modern.ink2,
        flex: "0 0 auto",
        whiteSpace: "nowrap",
        cursor: "pointer",

        /**
         * <Kbd/> is shared with the toolbar and the shortcut bar, so its own
         * size stays as it is; it is only trimmed here, inside a chip, where it
         * is what sets the chip's height rather than the label.
         */
        "& kbd": {
            font: `600 9.5px ${theme.modern.mono}`,
            padding: "1px 4px",
            borderBottomWidth: 1,
        },

        "&:hover": {
            borderColor: theme.modern.ink3,
            color: theme.modern.ink,
        },
        "&:active": {borderColor: theme.modern.accent},

        /**
         * The clinical conclusion the reviewer is recording, and so the tap in
         * this app with the worst consequence if it lands on the wrong chip.
         *
         * touchTarget() is not used: it centres its content, and these chips are
         * `textAlign: left` with a shortcut <kbd> trailing the label. Only the
         * height and the horizontal padding change; the 11 px label does not.
         */
        [COARSE]: {minHeight: TOUCH_MIN, padding: "0 14px"},
    },
    interp__active: {
        background: theme.modern.accent,
        borderColor: theme.modern.accent,
        color: theme.modern.onAccent,

        "&:hover": {color: theme.modern.onAccent, borderColor: theme.modern.accent},
        "& kbd": {
            background: "rgba(255,255,255,.2)",
            borderColor: "transparent",
            color: theme.modern.onAccent,
        },
    },
    // "Useful urgent clinical consultation" is the one option that escalates
    interp__urgent: {
        background: theme.modern.crit,
        borderColor: theme.modern.crit,

        "&:hover": {borderColor: theme.modern.crit},
    },
    interp__disabled: {
        cursor: "default",
        opacity: 0.6,

        "&:hover": {borderColor: theme.modern.line, color: theme.modern.ink2},
    },
    interp_text: {
        minWidth: 0,
    },

    input: {
        flex: "1 1 200px",
        minWidth: 170,
        height: 33,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 7,
        background: theme.modern.panel2,
        color: theme.modern.ink,
        font: `500 12.5px ${theme.modern.sans}`,
        padding: "0 11px",

        "&:focus": {
            outline: `2px solid ${theme.modern.accent}`,
            outlineOffset: -1,
        },
        "&::placeholder": {color: theme.modern.ink3},
        "&:disabled": {color: theme.modern.ink2, cursor: "default"},

        // the `font` shorthand above sets 12.5 px; this later rule overrides
        // only its size, so weight and family survive
        [COARSE]: touchField(),
    },
    input__dock: {
        flex: "none",
        width: "100%",
    },

    // ---- dock (sidebar layout) ----
    dock_body: {
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
        alignItems: "stretch",

        "@media (max-width:900px)": {gridTemplateColumns: "1fr"},
    },
    dock_findings: {
        padding: "10px 14px 11px",
        borderRight: `1px solid ${theme.modern.line}`,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,

        "@media (max-width:900px)": {
            borderRight: "none",
            borderBottom: `1px solid ${theme.modern.line}`,
        },
    },
    dock_dispo: {
        padding: "10px 14px 11px",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },
}))
